/**
 * BasicInfo
 *
 * Collects campaign title, description, and lead image with drag/drop upload,
 * size/type validation, and AI indicators. Supports read-only mode.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Label } from '../../ui/inputs/label';
import { Input } from '../../ui/inputs/input';
import { Textarea } from '../../ui/inputs/textarea';
import { Upload, X, Sparkles, Loader2, AlertCircle } from 'lucide-react';

export function BasicInfo({ 
  formData, 
  handleInputChange, 
  previewImage: propPreviewImage, 
  handleImageChange,
  handleRemoveImage,
  aiGeneratedFields,
  readOnly = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localPreviewImage, setLocalPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Use either the prop previewImage or our local state
  const previewImage = propPreviewImage || localPreviewImage;
  
  // Log component props on mount
  useEffect(() => {
    console.log('BasicInfo component mounted with props:', {
      formData,
      previewImage: propPreviewImage,
      handleImageChange: typeof handleImageChange === 'function' ? 'function' : typeof handleImageChange,
      handleRemoveImage: typeof handleRemoveImage === 'function' ? 'function' : typeof handleRemoveImage
    });
    
    // Initialize local preview image from prop if available
    if (propPreviewImage && !localPreviewImage) {
      setLocalPreviewImage(propPreviewImage);
    }
  }, []);
  
  // Log when previewImage changes
  useEffect(() => {
    console.log('Preview image updated:', {
      propPreviewImage,
      localPreviewImage,
      previewImage
    });
    setIsLoading(false);
  }, [previewImage, propPreviewImage, localPreviewImage]);
  
  // Update local preview when prop changes
  useEffect(() => {
    console.log('propPreviewImage changed:', propPreviewImage);
    if (propPreviewImage) {
      setLocalPreviewImage(propPreviewImage);
    }
  }, [propPreviewImage]);

  // Clear error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Self-contained image upload function
  const processImageUpload = (file) => {
    if (!file) {
      console.log('No file selected');
      setIsLoading(false);
      return;
    }
    
    // Check if file is an image
    if (!file.type.match('image.*')) {
      console.error('File is not an image:', file.type);
      setError({
        type: 'error',
        message: 'Please select an image file (PNG, JPG, GIF, etc.)'
      });
      setIsLoading(false);
      return;
    }
    
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      setError({
        type: 'error',
        message: 'Image size should be less than 5MB'
      });
      setIsLoading(false);
      return;
    }
    
    const reader = new FileReader();
    
    reader.onloadstart = () => {
      console.log('Starting to load image');
    };
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        console.log(`Loading progress: ${progress}%`);
      }
    };
    
    reader.onloadend = () => {
      console.log('Image loaded successfully');
      if (reader.result) {
        // Set the local preview image
        setLocalPreviewImage(reader.result);
        
        // IMPORTANT: We need to make sure the parent component gets the image data
        // This is critical for the PhonePreview component to display the image
        
        // First try to use the global setPreviewImage function
        if (window.setGlobalPreviewImage && typeof window.setGlobalPreviewImage === 'function') {
          console.log('Using global setPreviewImage function with image data');
          try {
            window.setGlobalPreviewImage(reader.result);
            console.log('Global setPreviewImage function called successfully');
            
            // Force a re-render after a short delay to ensure the state is updated
            setTimeout(() => {
              console.log('Forcing re-render after image update');
              setIsLoading(false);
            }, 100);
          } catch (err) {
            console.error('Error calling global setPreviewImage:', err);
          }
        }
        
        // Also try to use handleImageChange if it's available as a backup
        if (typeof handleImageChange === 'function') {
          try {
            console.log('Calling parent handleImageChange to update global state');
            
            // Call the parent's handleImageChange function with the file directly
            handleImageChange(file, setError);
          } catch (err) {
            console.error('Error calling handleImageChange:', err);
          }
        } else {
          console.warn('handleImageChange is not a function, using global setPreviewImage instead');
        }
      } else {
        console.error('Failed to load image: reader.result is null');
        setError({
          type: 'error',
          message: 'Failed to load image. Please try again.'
        });
        setIsLoading(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setError({
        type: 'error',
        message: 'Error reading image file. Please try again.'
      });
      setIsLoading(false);
    };
    
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Exception while reading file:', error);
      setError({
        type: 'error',
        message: 'Error processing image file. Please try again.'
      });
      setIsLoading(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsLoading(true);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      console.log('File dropped:', file);
      processImageUpload(file);
      e.dataTransfer.clearData();
    } else {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    console.log('handleFileInputChange called with event:', e);
    setIsLoading(true);
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log('File selected:', file);
      processImageUpload(file);
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Debug info */}
      {console.log('Rendering BasicInfo with:', {
        propPreviewImage,
        localPreviewImage,
        previewImage,
        isLoading
      })}
      <div>
        <Label htmlFor="title">Campaign Title *</Label>
        <div className="relative mt-2">
          <Input
            id="title"
            placeholder="Enter campaign title"
            value={formData.title}
            onChange={handleInputChange}
            autoComplete="off"
            data-form-type="other"
            required
            className={aiGeneratedFields?.title ? "border-purple-300 dark:border-purple-500" : ""}
            disabled={readOnly}
          />
          {aiGeneratedFields?.title && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-medium">AI</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="description">Campaign Description *</Label>
        <div className="relative mt-2">
          <Textarea
            id="description"
            placeholder="Describe your campaign's purpose and goals"
            value={formData.description}
            onChange={handleInputChange}
            required
            className={aiGeneratedFields?.description ? "border-purple-300 dark:border-purple-500" : ""}
            disabled={readOnly}
          />
          {aiGeneratedFields?.description && (
            <div className="absolute right-3 top-3 text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-medium">AI</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/50">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error.message}
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <Label>Campaign Image</Label>
        <div className="mt-2">
          <div 
            className="flex items-center justify-center w-full min-h-[160px]"
            onDragEnter={!readOnly ? handleDragEnter : undefined}
            onDragLeave={!readOnly ? handleDragLeave : undefined}
            onDragOver={!readOnly ? handleDragOver : undefined}
            onDrop={!readOnly ? handleDrop : undefined}
          >
            <div 
              onClick={!readOnly ? handleClick : undefined}
              className={`relative flex items-center justify-center w-full h-full border-2 ${isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-800'} ${isDragging ? 'border-solid' : 'border-dashed'} rounded-lg ${!readOnly ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''} transition-all duration-200`}
            >
              <div className="flex flex-col items-center justify-center p-6 w-full">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 mb-4 text-primary-500 dark:text-primary-400 animate-spin" />
                    <p className="text-sm text-primary-600 dark:text-primary-400">
                      Loading image...
                    </p>
                  </div>
                ) : previewImage ? (
                  <div className="relative w-full max-w-md group">
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-auto object-cover rounded-lg max-h-[200px] shadow-md"
                      onError={(e) => {
                        console.error('Error loading preview image:', e);
                        setError({ type: 'error', message: 'Failed to load image preview.' });
                      }}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Clear local preview image
                          setLocalPreviewImage(null);
                          
                          // Clear global preview image if available
                          if (window.setGlobalPreviewImage && typeof window.setGlobalPreviewImage === 'function') {
                            console.log('Clearing global preview image');
                            window.setGlobalPreviewImage(null);
                          }
                          
                          // Also call the prop function if available
                          if (typeof handleRemoveImage === 'function') {
                            handleRemoveImage();
                          } else {
                            console.error('handleRemoveImage is not a function');
                          }
                        }}
                        className="absolute -top-2 -right-2 p-1.5 rounded-full bg-red-500 text-white shadow-md opacity-90 hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {!readOnly ? (
                      <>
                        <Upload className={`w-8 h-8 mb-4 ${isDragging ? 'text-primary-500 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} />
                        <p className={`text-sm ${isDragging ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className={`mt-1 text-xs ${isDragging ? 'text-primary-500 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No image uploaded
                      </p>
                    )}
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleFileInputChange}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}